import React, { Component } from "react";
import yaml from "js-yaml";
import CopyURLSegment from "../../SharedComponents/CopyURLSegment";
import CustomAccordion from "../../SharedComponents/CustomAccordion";

/**
 * CLASS-BASED, PLAIN JS "API PLAYGROUND"
 * - OpenAPI 3.x (JSON or YAML)
 * - Editable path/query/header params + JSON body
 * - Request preview
 * - Code samples: curl, Python, JS, Ruby, PHP
 * - "Try It" button executes the request and shows response
 *
 * Props:
 *  - spec:            object (parsed OpenAPI)
 *  - specUrl:         string (URL to YAML or JSON)
 *  - specString:      string (raw YAML or JSON)
 *  - operationId:     string (preselect an operation)
 *  - languages:       array of strings: ["curl","python","javascript","ruby","php"]
 *  - fetcher:         (url, init) => Promise<Response> (to proxy around CORS)
 *  - className, style standard React props for styling container
 */

const DEFAULT_LANGS = ["curl", "python", "javascript", "ruby", "php"];
const HTTP_METHODS = ["get", "post", "put", "patch", "delete"];

class ApiPlaygroundClassic extends Component {
    constructor(props) {
        super(props);
        this.state = {
            rawSpec: props.spec || null,
            loadErr: null,

            // selection + request state
            selectedPath: "",
            selectedMethod: "",
            server: "",
            pathParams: {},
            queryParams: {},
            headerParams: {},
            contentType: "application/json",
            body: null,
            namedExamples: null,

            // UI state
            operations: [],
            activeLang: (props.languages && props.languages[0]) || DEFAULT_LANGS[0],
            resp: null,

            // paste fallback
            paste: "",
            loadingSpec: false,

            // New form state variables
            inputSheetName: "",
            inputCellName: "",
            inputCellValue: "",
            outputSheetName: "",
            outputCells: "",
            saveFilePostCall: false,

            // Accordion state
            showCodeSamples: true,

            isRequesting: false,
            formValues: {},
            
            // Separate visibility states for each key
            showAccessKey: false,
            showSecretKey: false,
            
            // Download state
            downloading: false,
        };
    }

    componentDidMount() {
        if (this.state.rawSpec) {
            this.initFromSpec(this.state.rawSpec);
        } else if (this.props.specUrl) {
            this.loadSpecFromUrl(this.props.specUrl);
        } else if (this.props.specString) {
            this.loadSpecFromString(this.props.specString);
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.spec && this.props.spec !== prevProps.spec) {
            this.setState({ rawSpec: this.props.spec, loadErr: null }, () => {
                this.initFromSpec(this.props.spec);
            });
        }
        if (this.props.specUrl && this.props.specUrl !== prevProps.specUrl) {
            this.loadSpecFromUrl(this.props.specUrl);
        }
        if (this.props.specString && this.props.specString !== prevProps.specString) {
            this.loadSpecFromString(this.props.specString);
        }
        
        if ((this.props.fileInformation !== prevProps.fileInformation || 
             this.props.fileHash !== prevProps.fileHash) && 
            this.state.rawSpec && this.state.selectedPath && this.state.selectedMethod) {
            
            const updatedPathParams = { ...this.state.pathParams };
            const updatedHeaderParams = { ...this.state.headerParams };
            
            if (this.props.fileHash) {
                Object.keys(updatedPathParams).forEach(key => {
                    if (key === "pk") {
                        updatedPathParams[key] = this.props.fileHash;
                    }
                });
            }
            
            // Update header parameters with access and secret keys
            if (this.props.fileInformation) {
                const fileKey = Object.keys(this.props.fileInformation)[0];
                const fileInfo = this.props.fileInformation[fileKey];
                
                if (fileInfo) {
                    if (fileInfo.accessKey && updatedHeaderParams.hasOwnProperty("AccessKey")) {
                        updatedHeaderParams["AccessKey"] = fileInfo.accessKey;
                    }
                    if (fileInfo.secretKey && updatedHeaderParams.hasOwnProperty("SecretKey")) {
                        updatedHeaderParams["SecretKey"] = fileInfo.secretKey;
                    }
                }
            }
            
            this.setState({ 
                pathParams: updatedPathParams,
                headerParams: updatedHeaderParams
            });
        }
    }

    // ----- Spec Loading -----
    loadSpecFromUrl = async (url) => {
        try {
            this.setState({ loadingSpec: true, loadErr: null });
            const res = await fetch(url);
            const text = await res.text();
            const parsed = await this.parseMaybeYaml(text);
            if (!parsed) throw new Error("Could not parse spec from URL.");
            this.setState({ rawSpec: parsed, loadingSpec: false }, () => this.initFromSpec(parsed));
        } catch (e) {
            this.setState({
                loadErr: e && e.message ? e.message : "Failed to load or parse spec from URL.",
                loadingSpec: false,
            });
        }
    };

    loadSpecFromString = async (str) => {
        try {
            const parsed = await this.parseMaybeYaml(str);
            if (!parsed) throw new Error("Could not parse spec string (YAML/JSON).");
            this.setState({ rawSpec: parsed, loadErr: null }, () => this.initFromSpec(parsed));
        } catch (e) {
            this.setState({ loadErr: e && e.message ? e.message : "Failed to parse spec string." });
        }
    };

    parseMaybeYaml = async (input) => {
        const trimmed = (input || "").trim();
        if (!trimmed) return null;
        if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
            try {
                return JSON.parse(trimmed);
            } catch (e) {
                // fall through to YAML
            }
        }
        try {
            return yaml.load(trimmed);
        } catch (e) {
            return null;
        }
    };

    // ----- Spec Helpers -----
    listOperations = (spec) => {
        const out = [];
        if (!spec || !spec.paths) return out;
        Object.keys(spec.paths).forEach((p) => {
            const item = spec.paths[p] || {};
            HTTP_METHODS.forEach((m) => {
                if (item[m]) out.push({ path: p, method: m, op: item[m] });
            });
        });
        return out;
    };

    resolveServer = (spec, operation, pathItem) => {
        if (operation && operation.servers && operation.servers[0] && operation.servers[0].url) {
            return operation.servers[0].url;
        }
        if (pathItem && pathItem.servers && pathItem.servers[0] && pathItem.servers[0].url) {
            return pathItem.servers[0].url;
        }
        if (spec.servers && spec.servers[0] && spec.servers[0].url) {
            return spec.servers[0].url;
        }
        return "";
    };

    requiredHeaderNames = (spec, op) => {
        const acc = {};
        const secReqs = []
            .concat(spec.security || [])
            .concat(op && op.security ? op.security : []);
        secReqs.forEach((req) => {
            Object.keys(req || {}).forEach((schemeName) => {
                const scheme = spec.components &&
                    spec.components.securitySchemes &&
                    spec.components.securitySchemes[schemeName];
                if (scheme && scheme.type === "apiKey" && scheme.in === "header" && scheme.name) {
                    acc[scheme.name] = true;
                }
            });
        });
        return Object.keys(acc);
    };

    seedFromSchema = (schema, depth) => {
        const d = depth || 0;
        if (!schema) return null;
        if (typeof schema !== "object") return null;
        if (schema.example !== undefined) return schema.example;
        if (schema.default !== undefined) return schema.default;
        if (schema.const !== undefined) return schema.const;

        var t = schema.type;
        if (Array.isArray(t)) t = t[0];

        if (schema.enum && schema.enum.length > 0) return schema.enum[0];

        if (t === "object" || (!t && schema.properties)) {
            const out = {};
            const props = schema.properties || {};
            Object.keys(props).forEach((k) => {
                out[k] = this.seedFromSchema(props[k], d + 1);
            });
            return out;
        }

        if (t === "array" || schema.items) {
            return [this.seedFromSchema(schema.items || {}, d + 1)];
        }

        if (t === "string") return "";
        if (t === "integer" || t === "number") return 0;
        if (t === "boolean") return false;
        return null;
    };

    initialStateFromOperation = (spec, path, method) => {
        const item = spec.paths && spec.paths[path];
        const op = item && item[method];
        const server = this.resolveServer(spec, op, item);

        const pathParams = {};
        const queryParams = {};
        const headerParams = {};
        (op.parameters || []).forEach((p) => {
            const example =
                p.example !== undefined
                    ? p.example
                    : (p.examples && Object.values(p.examples)[0] && Object.values(p.examples)[0].value);
            const v = example !== undefined ? example : (p.schema && p.schema.default) || "";
            if (p.in === "path") pathParams[p.name] = v;
            if (p.in === "query") queryParams[p.name] = v;
            if (p.in === "header") headerParams[p.name] = v;
        });

        headerParams["AccessKey"] = "";
        headerParams["SecretKey"] = "";

        if (this.props.fileInformation) {
            const fileKey = Object.keys(this.props.fileInformation)[0];
            const fileInfo = this.props.fileInformation[fileKey];
            
            if (fileInfo) {
                if (fileInfo.accessKey) {
                    headerParams["AccessKey"] = fileInfo.accessKey;
                }
                if (fileInfo.secretKey) {
                    headerParams["SecretKey"] = fileInfo.secretKey;
                }
            }
        }
        console.log("Final headerParams:", headerParams);

        this.requiredHeaderNames(spec, op).forEach((h) => {
            if (headerParams[h] === undefined) headerParams[h] = "";
        });

        // request body (prefer application/json)
        const content = op.requestBody && op.requestBody.content ? op.requestBody.content : {};
        const ctKey = Object.keys(content).find((k) =>
            k.toLowerCase().indexOf("application/json") !== -1
        ) || Object.keys(content)[0];

        var body = null;
        if (ctKey) {
            const entry = content[ctKey] || {};
            const example =
                entry.example !== undefined
                    ? entry.example
                    : (entry.examples &&
                        Object.values(entry.examples)[0] &&
                        Object.values(entry.examples)[0].value);
            body = example !== undefined ? example : this.seedFromSchema(entry.schema);
            
            // Override macro_name values with empty strings for better UX
            if (body && typeof body === 'object') {
                if (body.macros === "macro_name") {
                    body.macros = "";
                }
                if (body.post_formula_actions && Array.isArray(body.post_formula_actions)) {
                    body.post_formula_actions.forEach(action => {
                        if (action && action.parameters && action.parameters === "macro_name") {
                            action.parameters = "";
                        }
                    });
                }
            }
        }

        // collect named examples for JSON if present
        const namedExamples = {};
        if (content) {
            Object.keys(content).forEach((k) => {
                const v = content[k];
                if (v && v.examples && Object.keys(v.examples).length) {
                    namedExamples[k] = v.examples;
                }
            });
        }

        // when initializing from the spec (initialStateFromOperation)
        if (this.props.fileHash && pathParams && Object.prototype.hasOwnProperty.call(pathParams, "pk")) {
          pathParams.pk = this.props.fileHash;
        }

        return { server, pathParams, queryParams, headerParams, body, contentType: ctKey || "application/json", namedExamples };
    };

    // ----- URL Serialization -----
    toQueryString = (obj) => {
        const sp = new URLSearchParams();
        Object.keys(obj || {}).forEach((k) => {
            const v = obj[k];
            if (v !== undefined && v !== null && String(v) !== "") sp.append(k, String(v));
        });
        const qs = sp.toString();
        return qs ? "?" + qs : "";
    };

    fillPath = (path, pathParams) => {
        console.log(path);
        console.log(pathParams)
        return (path || "").replace(/\{([^}]+)\}/g, function (_, key) {
            const val = pathParams[key] != null ? String(pathParams[key]) : "";
            return encodeURIComponent(val);
        });
    };

    pretty = (v) => {
        try {
            return JSON.stringify(v, null, 2);
        } catch (e) {
            return String(v);
        }
    };

    // ----- Code Samples -----
    makeCodeSamples = (languages, method, url, headers, body, contentType) => {
        const langs = languages && languages.length ? languages : DEFAULT_LANGS;
        const upper = (method || "get").toUpperCase();
        // normalize headers for display (lowercase our custom keys)
        const rawPairs = Object.keys(headers || {})
            .filter((k) => headers[k] !== "" && headers[k] !== undefined && headers[k] !== null)
            .map((k) => [k, headers[k]]);

        const toDisplayKey = (k) => {
            const lk = String(k).toLowerCase();
            if (lk === "accesskey") return "accessKey";
            if (lk === "secretkey") return "secretKey";
            return k; // keep others as-is (e.g., Content-Type)
        };

        const hdrPairs = rawPairs.map(([k, v]) => [toDisplayKey(k), v]);
        const hdrObj = {};
        hdrPairs.forEach(([k, v]) => (hdrObj[k] = v));

        const isJsonBody = (upper === "POST" || upper === "PUT" || upper === "PATCH") &&
            contentType && contentType.indexOf("json") !== -1;

        // Use the current request body from the form instead of the old body state
        const currentRequestBody = this.buildRequestBody();
        const bodyJson = isJsonBody ? this.pretty(currentRequestBody) : "";

        // Helper function to wrap long lines
        const wrapLongLine = (line, maxLength = 80) => {
            if (line.length <= maxLength) return line;
            
            // Find a good breaking point (after commas, spaces, etc.)
            let breakPoint = maxLength;
            for (let i = maxLength; i > 0; i--) {
                if (line[i] === ',' || line[i] === ' ' || line[i] === '{' || line[i] === '}') {
                    breakPoint = i + 1;
                    break;
                }
            }
            
            return line.substring(0, breakPoint) + '\n  ' + line.substring(breakPoint);
        };

        const curl = [
            `curl -X ${upper} "${url}"`,
            hdrPairs.map(([k, v]) => `  -H "${k}: ${v}"`).join(" \\\n"),
            isJsonBody ? `  -H "Content-Type: ${contentType}"` : "",
            isJsonBody ? `  -d '${wrapLongLine(bodyJson)}'` : "",
        ].filter(Boolean).join(" \\\n");

        const python = [
            `# pip install requests`,
            `import requests`,
            `url = "${url}"`,
            `headers = ${this.pretty(hdrObj)}`,
            isJsonBody ? `payload = ${wrapLongLine(bodyJson)}` : ``,
            isJsonBody ? `resp = requests.${method}(url, json=payload, headers=headers)` : `resp = requests.${method}(url, headers=headers)`,
            `print(resp.status_code)`,
            `print(resp.text)`,
        ].filter(Boolean).join("\n");

        const javascript = [
            `fetch("${url}", {`,
            `  method: "${upper}",`,
            Object.keys(hdrObj).length ? `  headers: ${this.pretty(hdrObj)},` : ``,
            isJsonBody ? `  body: JSON.stringify(${wrapLongLine(bodyJson)}),` : ``,
            `})`,
            `.then(r => r.ok ? r.json().catch(() => r.text()) : Promise.reject(r))`,
            `.then(console.log)`,
            `.catch(async (err) => console.error(err.status, await err.text?.()))`,
        ].filter(Boolean).join("\n");

        const ruby = [
            `require 'net/http'`,
            `require 'json'`,
            `uri = URI("${url}")`,
            `http = Net::HTTP.new(uri.host, uri.port)`,
            `http.use_ssl = (uri.scheme == "https")`,
            `req = Net::HTTP::${upper.charAt(0) + upper.slice(1).toLowerCase()}.new(uri)`,
            hdrPairs.map(([k, v]) => `req["${k}"] = "${v}"`).join("\n"),
            isJsonBody ? `req.content_type = "${contentType}"\nreq.body = ${wrapLongLine(bodyJson)}.to_s` : ``,
            `res = http.request(req)`,
            `puts res.code`,
            `puts res.body`,
        ].filter(Boolean).join("\n");

        const php = [
            `<?php`,
            `$url = "${url}";`,
            `$ch = curl_init($url);`,
            `curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);`,
            Object.keys(hdrObj).length
                ? `curl_setopt($ch, CURLOPT_HTTPHEADER, ${this.pretty(Object.keys(hdrObj).map((k) => k + ": " + hdrObj[k])).replace(/\n/g,"")});`
                : ``,
            `curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "${upper}");`,
            isJsonBody ? `$payload = ${wrapLongLine(bodyJson)};\ncurl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($payload));` : ``,
            `$resp = curl_exec($ch);`,
            `$status = curl_getinfo($ch, CURLINFO_HTTP_CODE);`,
            `curl_close($ch);`,
            `echo $status . "\\n";`,
            `echo $resp;`,
            `?>`,
        ].filter(Boolean).join("\n");

        const map = {
            curl, python, javascript, ruby, php
        };
        const result = {};
        (langs || DEFAULT_LANGS).forEach((l) => { result[l] = map[l]; });
        return result;
    };

    // ----- Init from spec -----
    initFromSpec = (spec) => {
        const operations = this.listOperations(spec);
        if (!operations.length) {
            this.setState({ operations, selectedPath: "", selectedMethod: "", loadErr: "No operations found in spec." });
            return;
        }
        let pick = operations[0];
        if (this.props.operationId) {
            const found = operations.find((o) => o.op && o.op.operationId === this.props.operationId);
            if (found) pick = found;
        }
        const init = this.initialStateFromOperation(spec, pick.path, pick.method);
        var bodySchema = this.getRequestBodySchema(spec, pick.path, pick.method);
        var top = {};
        var params = this.getRequestBodyParams(spec, pick.path, pick.method);
        if (params.length) {
          for (var i=0;i<params.length;i++) {
            var p = params[i];
            top[p.name] = this.seedFlat(p.schema);
          }
          // Override macros field to remove "macro_name" placeholder
          if (top.macros === "macro_name") {
            top.macros = "";
          }
        } else if (bodySchema) {
          top["(root)"] = this.seedFlat(bodySchema); // non-object body
          // Also check root level for macros
          if (top["(root)"] && top["(root)"].macros === "macro_name") {
            top["(root)"].macros = "";
          }
        }
        this.setState({
            operations,
            selectedPath: pick.path,
            selectedMethod: pick.method,
            server: init.server,
            pathParams: init.pathParams,
            queryParams: init.queryParams,
            headerParams: init.headerParams,
            body: init.body,
            contentType: init.contentType || "application/json",
            namedExamples: init.namedExamples || null,
            resp: null,
            formValues: top
        });
    };

    // ----- UI Handlers -----
    handleOperationChange = (e) => {
        const value = e.target.value; // "method path"
        const space = value.indexOf(" ");
        const method = value.slice(0, space);
        const path = value.slice(space + 1);
        const { rawSpec } = this.state;
        const init = this.initialStateFromOperation(rawSpec, path, method);
        var bodySchema = this.getRequestBodySchema(this.state.rawSpec, path, method);
        var top = {};
        var params = this.getRequestBodyParams(this.state.rawSpec, path, method);
        if (params.length) {
          for (var i=0;i<params.length;i++) {
            var p = params[i];
            top[p.name] = this.seedFlat(p.schema);
          }
          // Override macros field to remove "macro_name" placeholder
          if (top.macros === "macro_name") {
            top.macros = "";
          }
        } else if (bodySchema) {
          top["(root)"] = this.seedFlat(bodySchema);
          // Also check root level for macros
          if (top["(root)"] && top["(root)"].macros === "macro_name") {
            top["(root)"].macros = "";
          }
        }
        this.setState({
            selectedPath: path,
            selectedMethod: method,
            server: init.server,
            pathParams: init.pathParams,
            queryParams: init.queryParams,
            headerParams: init.headerParams,
            body: init.body,
            contentType: init.contentType || "application/json",
            namedExamples: init.namedExamples || null,
            resp: null,
            formValues: top
        });
    };

    handleParamChange = (kind, key, value) => {
        const obj = Object.assign({}, this.state[kind]);
        obj[key] = value;
        this.setState({ [kind]: obj });
    };

    handleBodyChange = (e) => {
        const text = e.target.value;
        try {
            const parsed = JSON.parse(text);
            this.setState({ body: parsed });
        } catch (err) {
            // keep raw until valid
            this.setState({ body: text });
        }
    };

    handleUseExample = (e) => {
        const key = e.target.value; // example key
        const { namedExamples } = this.state;
        if (!namedExamples) return;
        // Prefer first JSON-like content type
        const ct = Object.keys(namedExamples).find((k) => k.toLowerCase().indexOf("application/json") !== -1) ||
            Object.keys(namedExamples)[0];
        if (!ct) return;
        const ex = namedExamples[ct] && namedExamples[ct][key];
        if (ex && ex.value !== undefined) {
            this.setState({ body: ex.value, contentType: ct });
        }
    };

    tryIt = async () => {
        const {
            selectedMethod, selectedPath, pathParams, queryParams, headerParams,
            contentType, server
        } = this.state;

        if (!selectedMethod || !selectedPath) return;

        const needsPk = /\{pk\}/.test(selectedPath || "");
        if (needsPk && (!pathParams || !String(pathParams.pk || "").trim())) {
          if (this.props.fileHash) {
            const pp = { ...(pathParams || {}), pk: this.props.fileHash };
            this.setState({ pathParams: pp });
          } else {
            this.setState({ resp: { error: "Missing File ID (pk). Enter it or open a file first." } });
            return;
          }
        }

        const filled = this.fillPath(selectedPath, this.state.pathParams);
        const url = (server || "").replace(/\/+$/, "") + filled + this.toQueryString(queryParams);

        let ak = headerParams.AccessKey || headerParams.accessKey;
        let sk = headerParams.SecretKey || headerParams.secretKey;
        if ((!ak || !sk) && this.props.fileInformation) {
          const fileKey = Object.keys(this.props.fileInformation)[0] || "";
          const info = this.props.fileInformation[fileKey] || {};
          ak = ak || info.accessKey;
          sk = sk || info.secretKey;
        }
        ak = (ak || "").toString().trim();
        sk = (sk || "").toString().trim();

        if (!ak || !sk) {
          this.setState({ resp: { error: "Missing accessKey/secretKey. Connect a file or ensure credentials exist." } });
          return;
        }

        const method = selectedMethod.toUpperCase();
        const init = {
          method,
          headers: {
            accessKey: ak,
            secretKey: sk,
            "Content-Type": "application/json",
            Accept: "application/json"
          }
        };

        // Only attach body for JSON methods
        if (method === "POST" || method === "PUT" || method === "PATCH") {
          init.body = JSON.stringify(this.buildRequestBody());
        }

        this.setState({ isRequesting: true, resp: null });
        try {
            const fx = this.props.fetcher || fetch;
            const r = await fx(url, init);

            const headersObj = {};
            try { r.headers.forEach((v, k) => (headersObj[k] = v)); } catch (_) {}

            const ctype = (r.headers && r.headers.get && r.headers.get("content-type")) || "";
            let parsed;
            try {
                if (ctype.indexOf("application/json") !== -1) parsed = await r.json();
                else parsed = await r.text();
            } catch (e) {
                parsed = await r.text().catch(() => "");
            }

            this.setState({ resp: { status: r.status, headers: headersObj, body: parsed, error: null }, isRequesting: false });
        } catch (e) {
            this.setState({ resp: { error: e && e.message ? e.message : String(e) }, isRequesting: false });
        }
    };

    safeParse = (s) => {
        try {
            return JSON.parse(s);
        } catch (e) {
            return s;
        }
    };

    toggleCodeSamples = () => {
        this.setState((prev) => ({ showCodeSamples: !prev.showCodeSamples }));
    };

    copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            // Optional: show a brief success message
            console.log('URL copied to clipboard');
        }).catch(err => {
            console.error('Failed to copy: ', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
        });
    }

    renderFieldset(title, children, extraStyle) {
        return (
            <fieldset style={Object.assign({ border: "1px solid #eee", padding: 12, borderRadius: 8, marginTop: 12 }, extraStyle)}>
                <legend style={{ fontWeight: 700, fontSize: "18px" }}>{title}</legend>
                {children}
            </fieldset>
        );
    }

    renderLabeledInput(label, value, onChange) {
        const displayName = label === "pk" ? "File ID" : label;
        
        const isCredential = label === "AccessKey" || label === "SecretKey";
        
        return (
            <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 8, alignItems: "center", marginBottom: 8 }}>
                <label style={{ fontWeight: 600 }}>{displayName}</label>
                <input
                    type="text" 
                    value={value == null ? "" : value}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={isCredential} 
                    style={{ 
                        display: "block", 
                        width: "100%", 
                        marginTop: 4, 
                        border: "none",
                        borderRadius: 4, 
                        padding: 6,
                        backgroundColor: isCredential ? "#F0F0F0" : "white" 
                    }}
                />
            </div>
        );
    }

    // ---- OpenAPI helpers: resolve $ref and find requestBody schema ----
    resolveRef = (spec, schema) => {
      if (!schema) return null;
      if (schema.$ref) {
        var ref = schema.$ref.replace(/^#\//, "").split("/");
        var cur = spec;
        for (var i = 0; i < ref.length; i++) {
          var seg = ref[i];
          if (cur && typeof cur === "object") cur = cur[seg];
          else { cur = undefined; break; }
        }
        return this.resolveRef(spec, cur);
      }
      if (schema.allOf && schema.allOf.length) {
        var out = {};
        for (var j = 0; j < schema.allOf.length; j++) {
          var part = this.resolveRef(spec, schema.allOf[j]);
          if (part && typeof part === "object") {
            for (var k in part) out[k] = part[k];
          }
        }
        return out;
      }
      return schema;
    };
    
    // Human-friendly labels for common field keys
    prettyLabel = (name) => {
      var n = String(name || "");
      if (n === "sheet_name") return "Sheet Name";
      if (n === "cell_name") return "Named Cell or Cell";
      if (n === "cell_value") return "Cell Value";
      if (n === "cells") return "Cells";
      return n.replace(/_/g, " ").replace(/^\w/, function (c) { return c.toUpperCase(); });
    };
    
    getRequestBodySchema = (spec, path, method) => {
      var item = (spec.paths && spec.paths[path]) || null;
      if (!item) return null;
      var op = item[method] || null;
      if (!op || !op.requestBody || !op.requestBody.content) return null;

      var content = op.requestBody.content;
      var keys = Object.keys(content);
      var ctKey = null;
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i];
        if (k.toLowerCase().indexOf("application/json") !== -1) { ctKey = k; break; }
      }
      if (!ctKey && keys.length) ctKey = keys[0];
      if (!ctKey) return null;

      var entry = content[ctKey] || {};
      var schema = entry.schema || null;
      return this.resolveRef(spec, schema);
    };

    // Return top-level request body params: [{ name, required, schema }]
    getRequestBodyParams = (spec, path, method) => {
      var schema = this.getRequestBodySchema(spec, path, method);
      if (!schema) return [];
      var params = [];
      var required = (schema.required && Array.isArray(schema.required)) ? schema.required : [];

      if ((schema.type === "object") || schema.properties) {
        var props = schema.properties || {};
        for (var name in props) {
          var propSchema = this.resolveRef(spec, props[name]);
          params.push({ name: name, required: required.indexOf(name) !== -1, schema: propSchema });
        }
      } else {
        params.push({ name: "(root)", required: true, schema: schema });
      }
      return params;
    };

    // Styled param boxes (match Input/Output/Save cards)
    renderSimpleParamBoxes = (spec, path, method) => {
      var params = this.getRequestBodyParams(spec, path, method);
      if (!params.length) return null;

      return (
        <div>
          {params.map(function (p) {
            var label = p.name + (p.required ? " *" : "");
            var desc = (p.schema && p.schema.description) ? String(p.schema.description) : "";

            return (
              <div key={p.name} style={{ marginBottom: 20 }}>
                <h3 style={{ margin: "0 0 12px 0", color: "#000", fontSize: "20px" }}>{label}</h3>
                <div style={{
                  backgroundColor: "#fff",
                  padding: 16,
                  borderRadius: 4,
                  border: "1px solid #e9ecef"
                }}>
                  {desc ? <div style={{ color: "#666", fontSize: "13px" }}>{desc}</div> : null}
                </div>
              </div>
            );
          }.bind(this))}
        </div>
      );
    };

    renderParamCardsWithBasicInputs = (spec, path, method) => {
      var schema = this.getRequestBodySchema(spec, path, method);
      if (!schema) return null;
      var params = this.getRequestBodyParams(spec, path, method);
      if (!params.length) {
        // whole body
        var t = schema.type; if (t && t.splice && t.length) t = t[0];
        var v = this.state.formValues["(root)"];
        var isPrimitive = (t === "string" || t === "number" || t === "integer" || t === "boolean");
        return (
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ margin: "0 0 12px 0", color: "#000", fontSize: "20px" }}>Body</h3>
            <div style={{ backgroundColor: "#fff", padding: 16, borderRadius: 4, border: "1px solid #e9ecef" }}>
              {isPrimitive ? (
                <input
                  type={(t === "number" || t === "integer") ? "number" : (t === "boolean" ? "checkbox" : "text")}
                  {...((t === "boolean")
                    ? { checked: !!v, onChange: (e)=> this.setState({ formValues: (function(){ var n = {}; for (var k in (v||{})){} n["(root)"]=e.target.checked; return n; }()) }) }
                    : { value: (v !== undefined && v !== null) ? v : ((t==="number"||t==="integer")?0:""),
                        onChange:(e)=>{ var n={}; n["(root)"]=(t==="number"||t==="integer")?Number(e.target.value):e.target.value; this.setState({ formValues:n }); } })}
                  style={{ width: (t==="boolean")?"auto":"100%", padding: (t==="boolean")?0:8, border: "1px solid #ddd", borderRadius: 4 }}
                />
              ) : (
                <textarea
                  rows={8}
                  value={typeof v === "string" ? v : JSON.stringify(v || {}, null, 2)}
                  onChange={(e)=> {
                    var parsed = e.target.value;
                    try { parsed = JSON.parse(e.target.value); } catch(_) {}
                    var n = {}; n["(root)"] = parsed; this.setState({ formValues: n });
                  }}
                  style={{ width: "100%", fontFamily: "monospace", border: "1px solid #ddd", borderRadius: 4, padding: 8 }}
                />
              )}
            </div>
          </div>
        );
      }

      // object body → one card per top-level property
      var self = this;
      return (
        <div>
          {params.map(function(p){
            var ps = self.resolveRef(spec, p.schema);
            var t = ps && ps.type; if (t && t.splice && t.length) t=t[0];
            var v = self.state.formValues[p.name];
            var isPrimitive = (t === "string" || t === "number" || t === "integer" || t === "boolean");
            var isEnum = (ps && ps.enum && ps.enum.length);

            return (
              <div key={p.name} style={{ marginBottom: 20 }}>
                <h3 style={{ margin: "0 0 12px 0", color: "#000", fontSize: "20px" }}>
                  {p.name}
                </h3>
                <div style={{ backgroundColor: "#fff", padding: 16, borderRadius: 4, border: "1px solid #e9ecef" }}>
                  {isEnum ? (
                    <select
                      value={(v !== undefined && v !== null) ? v : ""}
                      onChange={(e)=>{ var next = Object.assign({}, self.state.formValues); next[p.name] = e.target.value; self.setState({ formValues: next }); }}
                      style={{ width: "100%", padding: 8, borderRadius: 4, background: "#F0F0F0", border: "none", color:"#666"  }}
                    >
                      <option value="">Select…</option>
                      {ps.enum.map(function(opt){ return <option key={String(opt)} value={opt}>{String(opt)}</option>; })}
                    </select>
                  ) : isPrimitive ? (
                    <input
                      type={(t === "number" || t === "integer") ? "number" : (t === "boolean" ? "checkbox" : "text")}
                      {...((t === "boolean")
                        ? { checked: !!v, onChange:(e)=>{ var next=Object.assign({}, self.state.formValues); next[p.name]=e.target.checked; self.setState({ formValues: next }); } }
                        : { value:(v !== undefined && v !== null) ? v : ((t==="number"||t==="integer")?0:""),
                            onChange:(e)=>{ var next=Object.assign({}, self.state.formValues); next[p.name]=(t==="number"||t==="integer")?Number(e.target.value):e.target.value; self.setState({ formValues: next }); } })}
                      style={{ width:(t==="boolean")?"auto":"100%", padding:(t==="boolean")?0:8, border:"1px solid #ddd", borderRadius:4 }}
                    />
                  ) : (
                    <textarea
                      rows={8}
                      value={typeof v === "string" ? v : JSON.stringify(v || (t==="array"?[]:{}), null, 2)}
                      onChange={(e)=>{
                        var parsed = e.target.value;
                        try { parsed = JSON.parse(e.target.value); } catch(_) {}
                        var next = Object.assign({}, self.state.formValues);
                        next[p.name] = parsed;
                        self.setState({ formValues: next });
                      }}
                      style={{ width: "100%", fontFamily: "monospace", borderRadius: 4, padding: 8, background: "#F0F0F0", border: "none", color:"#666" }}
                    />
                  )}
                  {ps && ps.description ? <small style={{ opacity: 0.7 }}>{ps.description}</small> : null}
                </div>
              </div>
            );
          })}
        </div>
      );
    };

    // ----- Render -----
    render() {
        const {
            rawSpec, loadErr, loadingSpec, operations, selectedPath, selectedMethod,
            server, pathParams, queryParams, headerParams, contentType, body, namedExamples,
            activeLang, resp, paste
        } = this.state;

        if (!rawSpec) {
            return (
                <div className={this.props.className} style={Object.assign({ padding: 16, border: "1px solid #eee", borderRadius: 8 }, this.props.style)}>
                    <h3 style={{ marginTop: 0 }}>API Playground</h3>
                    {loadErr ? <div style={{ color: "crimson", marginBottom: 8 }}>{loadErr}</div> : null}
                    {this.props.specUrl ? (
                        <div>{loadingSpec ? "Loading…" : "Nothing to load yet."}</div>
                    ) : (
                        <div>
                            <p>Paste your OpenAPI 3.x (JSON or YAML):</p>
                            <textarea
                                value={paste}
                                onChange={(e) => this.setState({ paste: e.target.value })}
                                rows={12}
                                style={{ width: "100%", fontFamily: "monospace" }}
                                placeholder="Paste OpenAPI here…"
                            />
                            <div style={{ marginTop: 8 }}>
                                <button onClick={() => this.loadSpecFromString(paste)}>Use this spec</button>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        const specInfo = (rawSpec.info && (rawSpec.info.title || rawSpec.info.version)) ? (
            <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                <h3 style={{ 
                    margin: 0, 
                    flex: "1 1 auto",
                    fontFamily: "Lato",
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "rgb(22, 30, 22)"
                }}>
                    {rawSpec.info.title}
                </h3>
                <small style={{ opacity: 0.7 }}>{rawSpec.openapi ? ("OpenAPI " + rawSpec.openapi) : "OpenAPI 3.x"}</small>
            </div>
        ) : <h3 style={{ marginTop: 0 }}>API Playground</h3>;

        const items = operations.map(({ path, method, op }) => ({
            key: method.toUpperCase() + " " + path,
            label: method.toUpperCase() + " " + path,
            summary: (op && (op.summary || op.operationId)) || "",
            value: method + " " + path
        }));

        const pathFilled = this.fillPath(selectedPath, pathParams);
        const url = (server || "").replace(/\/+$/, "") + pathFilled + this.toQueryString(queryParams);

        const code = selectedPath && selectedMethod
            ? this.makeCodeSamples(this.props.languages || DEFAULT_LANGS, selectedMethod, url, headerParams, (typeof body === "string" ? this.safeParse(body) : body), contentType || "application/json")
            : {};

        // Pull JSON-named examples list, if any
        let jsonExamples = [];
        if (namedExamples) {
            // prefer JSON content-type bucket
            const jsonKey = Object.keys(namedExamples).find((k) => k.toLowerCase().indexOf("application/json") !== -1) ||
                Object.keys(namedExamples)[0];
            if (jsonKey) {
                jsonExamples = Object.keys(namedExamples[jsonKey] || {}).map((k) => ({
                    key: k,
                    name: k,
                    summary: (namedExamples[jsonKey][k] && namedExamples[jsonKey][k].summary) || k
                }));
            }
        }

        const isAiPage = rawSpec && (
            (rawSpec.info && rawSpec.info.title === "AI Prompt") ||
            selectedPath.includes("/ai/")
        );

        return (
            <div className={this.props.className} style={Object.assign({ padding: 16, color: "#333", minWidth: 0 }, this.props.style)}>
                {specInfo}

                <div style={{ marginTop: 16, marginBottom: 20 }}>
                    {isAiPage ? (
                        <div className="codeFileURL" id="fileURL">
                            <div style={{marginBottom: "6px", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                <span>
                                    <span style={{verticalAlign: "middle", fontSize: "16px", color: "#161E16", fontWeight: "700", fontFamily: "Lato"}}>
                                        <i className='bx bx-window-alt' style={{color: "#9696A0", fontSize: "20px", verticalAlign: "middle"}} />
                                        <span style={{paddingLeft: "4px", marginTop: "5px"}}>API URL
                                            <span style={{ backgroundColor: "#FFA500", color: "white", fontSize: "85%", padding: "2px 4px", fontWeight: "700", marginLeft: "6px", borderRadius: "4px" }}>
                                                POST
                                            </span>
                                        </span>
                                    </span>
                                </span>
                                <button
                                    onClick={this.handleDownloadSpec}
                                    disabled={this.state.downloading}
                                    style={{
                                        background: "#6FBB98",
                                        color: "white",
                                        border: "none",
                                        padding: "8px 12px",
                                        borderRadius: "4px",
                                        cursor: this.state.downloading ? "not-allowed" : "pointer",
                                        opacity: this.state.downloading ? 0.7 : 1,
                                        fontSize: "12px",
                                        fontWeight: "600"
                                    }}
                                >
                                    {this.state.downloading ? "Downloading..." : "Download OpenAPI (.yaml)"}
                                </button>
                            </div>
                            <p className="apiURLText" id="apiURLInput">
                                <i className="copy outline icon fileIDLabelIcon" 
                                   style={{color: "green", cursor: "pointer", marginRight: "8px"}}
                                   onClick={() => {
                                       navigator.clipboard.writeText(`${server}${this.fillPath(selectedPath, pathParams)}`);
                                       console.log("URL Copied!");
                                   }}
                                />
                                {`${server}${this.fillPath(selectedPath, pathParams)}`}
                            </p>
                        </div>
                    ) : (
                        <CopyURLSegment
                            url={`${server}${this.fillPath(selectedPath, pathParams)}`}
                            header={<span>API URL
                                <span style={{ backgroundColor: "#FFA500", color: "white", fontSize: "85%", padding: "2px 4px", fontWeight: "700", marginLeft: "6px", borderRadius: "4px" }}>
                                    POST
                                </span></span>}
                        />
                    )}
                </div>

                {/* Headers */}
                <div style={{ marginTop: 12, marginBottom: 20 }}>
                    <h3 style={{ margin: "0 0 12px 0", color: "#161e16", fontSize: "16px", fontWeight: "600" }}>Headers</h3>
                    <div style={{
                        backgroundColor: "#fff",
                        padding: 16,
                        borderRadius: 4,
                        border: "1px solid #e9ecef"
                    }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "end" }}>
                            <div>
                                <label style={{ fontWeight: 600, display: "block", marginBottom: 4, color: "#1c1c1c" }}>Access Key</label>
                                <div style={{ position: "relative" }}>
                                    <input
                                        type={this.state.showAccessKey ? "text" : "password"}
                                        value={(this.state.headerParams.AccessKey || this.state.headerParams.accessKey || "")}
                                        disabled={true}
                                        style={{
                                            width: "100%",
                                            padding: "8px 40px 8px 8px", // Add right padding for button
                                            border: "none",
                                            borderRadius: 4,
                                            backgroundColor: "#f5f5f5",
                                            color: "#666"
                                        }}
                                    />
                                    <button
                                        onClick={this.toggleAccessKeyVisibility}
                                        style={{
                                            position: "absolute",
                                            right: "4px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: "4px",
                                            borderRadius: "2px",
                                            color: "#666",
                                            fontSize: "14px"
                                        }}
                                        title={this.state.showAccessKey ? "Hide Access Key" : "Show Access Key"}
                                    >
                                        👁️‍🗨️
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label style={{ fontWeight: 600, display: "block", marginBottom: 4, color: "#1c1c1c" }}>Secret Key</label>
                                <div style={{ position: "relative" }}>
                                    <input
                                        type={this.state.showSecretKey ? "text" : "password"}
                                        value={(this.state.headerParams.SecretKey || this.state.headerParams.secretKey || "")}
                                        disabled={true}
                                        style={{
                                            width: "100%",
                                            padding: "8px 40px 8px 8px", // Add right padding for button
                                            border: "none",
                                            borderRadius: 4,
                                            backgroundColor: "#f5f5f5",
                                            color: "#666"
                                        }}
                                    />
                                    <button
                                        onClick={this.toggleSecretKeyVisibility}
                                        style={{
                                            position: "absolute",
                                            right: "4px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: "4px",
                                            borderRadius: "2px",
                                            color: "#666",
                                            fontSize: "14px"
                                        }}
                                        title={this.state.showSecretKey ? "Hide Secret Key" : "Show Secret Key"}
                                    >
                                        👁️‍🗨️
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ 
                    display: "grid", 
                    gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", 
                    gap: 24, 
                    alignItems: "start"
                }}>
                    {/* Left Column: Request Params */}
                    <div>
                        <h3 style={{ margin: "0 0 12px 0", color: "#161e16", fontSize: "16px",fontWeight: "600" }}>Request Parameters</h3>
                        {this.renderParamCardsWithInputs(this.state.rawSpec, selectedPath, selectedMethod)}
                    </div>
                    
                    {/* Right Column: Request Preview */}
                    <div>
                        <div style={{ maxWidth: "100%" }}>
                            <h3 style={{ margin: "0 0 12px 0", color: "#161e16", fontSize: "16px", fontWeight: "600" }}>Request Preview</h3>
                            <div style={{ 
                                backgroundColor: "#fff", 
                                padding: 16, 
                                borderRadius: 4, 
                                border: "1px solid #e9ecef" 
                            }}>
                                {(selectedMethod && (selectedMethod === "post" || selectedMethod === "put" || selectedMethod === "patch")) ? (
                                    <div>
                                        <div style={{ fontWeight: 600, marginBottom: 8, color: "#1c1c1c" }}>Request Body:</div>
                                        <pre style={{ 
                                            background: "#0B1020", 
                                            padding: 12, 
                                            borderRadius: 4, 
                                            margin: 0,
                                            fontSize: "13px",
                                            lineHeight: "1.4",
                                            overflowX: "auto",
                                            maxHeight: "354px",
                                            overflowY: "auto",
                                            color: "#fff"
                                        }}>
                                            {JSON.stringify(this.buildRequestBody(), null, 2)}
                                        </pre>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Code Samples */}
                <CustomAccordion
                    open
                    header={<span>Code Samples</span>}
                    body={
                        <div style={{ minWidth: 0 }}>
                            <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                                {(this.props.languages || DEFAULT_LANGS).map((l) => (
                                    <button
                                        key={l}
                                        onClick={() => this.setState({ activeLang: l })}
                                        style={{
                                            padding: "6px 10px",
                                            borderRadius: 6,
                                            border: "1px solid #ddd",
                                            background: l === activeLang ? "#e7f5ef" : "white",
                                            cursor: "pointer",
                                        }}
                                    >
                                        {l}
                                    </button>
                                ))}
                            </div>

                            <div style={{ minWidth: 0, overflowX: "auto", overflowY: "auto", maxHeight: "356px", width: "100%" }}>
                                <pre style={{
                                    margin: 0,
                                    background: "#0b1020",
                                    color: "white",
                                    padding: 12,
                                    borderRadius: 6,
                                    whiteSpace: "pre-wrap",
                                    overflowWrap: "anywhere",
                                    wordBreak: "break-word",
                                    maxHeight: "356px"
                                }}>
                                    {code[activeLang]}
                                </pre>
                            </div>
                        </div>
                    }
                    accordionStyle={{
                        borderRadius: '4px',
                        border: '1px solid #F0F0F0',
                        backgroundColor: 'white',
                        marginTop: 20
                    }}
                    headerDivStyle={{
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        fontSize: '16px',
                        fontWeight: '700',
                        fontFamily: 'Lato',
                        padding: '16px'
                    }}
                />

                {/* Try It */}
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 20 }}>
                    <button
                        onClick={this.tryIt}
                        disabled={this.state.isRequesting}
                        style={{ background: "#6FBB98", color: "white", padding: "8px 12px", border: 0, borderRadius: 6, cursor: this.state.isRequesting ? "not-allowed" : "pointer", opacity: this.state.isRequesting ? 0.7 : 1 }}
                    >
                        {this.state.isRequesting ? "Sending…" : "Try It"}
                    </button>
                    
                </div>

                {/* Response */}
                {resp ? (
                <CustomAccordion
                    open
                    header={<span>Response{resp.status !== undefined ? ` — Status: ${resp.status}` : ""}</span>}
                    body={
                    resp.error ? (
                        <div style={{ color: "crimson" }}>Error: {resp.error}</div>
                    ) : (
                        <div>
                        {resp.headers ? (
                            <div style={{ marginTop: 8 }}>
                            <div style={{ fontWeight: 600 }}>Headers</div>
                            <pre style={{ background: "#f7f7f7", padding: 12, borderRadius: 6 }}>{this.pretty(resp.headers)}</pre>
                            </div>
                        ) : null}
                        <div style={{ marginTop: 8, fontWeight: 600 }}>Body</div>
                        <pre style={{ background: "#f7f7f7", padding: 12, borderRadius: 6 }}>
                            {typeof resp.body === "string" ? resp.body : this.pretty(resp.body)}
                        </pre>
                        </div>
                    )
                    }
                    accordionStyle={{
                    borderRadius: '4px',
                    border: '1px solid #F0F0F0',
                    backgroundColor: 'white',
                    marginTop: 20
                    }}
                    headerDivStyle={{
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    fontSize: '16px',
                    fontWeight: '700',
                    fontFamily: 'Lato',
                    padding: '16px'
                    }}
                />
                ) : null}
            </div>
        );
    }

    // Parse OpenAPI schema and build form elements
    buildDynamicForm = (spec) => {
        if (!spec || !spec.components || !spec.components.schemas) return null;
        
        // Look for CalculateRequest schema
        const calculateSchema = spec.components.schemas.CalculateRequest;
        if (!calculateSchema) return null;
        
        return this.buildFormFromSchema(calculateSchema);
    };

    buildFormFromSchema = (schema) => {
        if (!schema.properties) return null;
        
        const formElements = [];
        
        // Build input_cells section
        if (schema.properties.input_cells) {
            formElements.push(this.buildInputCellsSection(schema.properties.input_cells));
        }
        
        // Build output_cells section
        if (schema.properties.output_cells) {
            formElements.push(this.buildOutputCellsSection(schema.properties.output_cells));
        }
        
        // Build save_file_post_call section
        if (schema.properties.save_file_post_call) {
            formElements.push(this.buildSaveFileSection(schema.properties.save_file_post_call));
        }
        
        return formElements;
    };

    buildInputCellsSection = (inputCellsSchema) => {
        const sheetNames = this.getSheetNames();
        
        return (
            <div key="input_cells" style={{ marginBottom: 20 }}>
                <h3 style={{ margin: "0 0 12px 0", color: "#000", fontSize: "20px" }}>Input Cells</h3>
                <div style={{ 
                    backgroundColor: "#fff", 
                    padding: 16, 
                    borderRadius: 4, 
                    border: "1px solid #e9ecef" 
                }}>
                    <div style={{ 
                        display: "grid", 
                        gridTemplateColumns: "1fr 1fr 1fr", 
                        gap: 16, 
                        alignItems: "end" 
                    }}>
                        <div>
                            <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Sheet Name</label>
                            <select 
                                value={this.state.inputSheetName || ""} 
                                onChange={(e) => this.setState({ inputSheetName: e.target.value })}
                                style={{ width: "100%", padding: 8, borderRadius: 4, background: "#F0F0F0", border: "none", color:"#666" }}
                            >
                                <option value="">Select sheet name</option>
                                {sheetNames.map(name => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div>
                            <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Cell Name</label>
                            <input 
                                type="text" 
                                placeholder="e.g., A2, B3, or named range"
                                value={this.state.inputCellName || ""} 
                                onChange={(e) => this.setState({ inputCellName: e.target.value })}
                                style={{ width: "100%", padding: 8, border: "1px solid #ddd", borderRadius: 4 }}
                            />
                        </div>
                        
                        <div>
                            <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Cell Value</label>
                            <input 
                                type="text" 
                                placeholder="Value to set in the cell"
                                value={this.state.inputCellValue || ""} 
                                onChange={(e) => this.setState({ inputCellValue: e.target.value })}
                                style={{ width: "100%", padding: 8, border: "1px solid #ddd", borderRadius: 4 }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    buildOutputCellsSection = (outputCellsSchema) => {
        const sheetNames = this.getSheetNames();
        
        return (
            <div key="output_cells" style={{ marginBottom: 20 }}>
                <h3 style={{ margin: "0 0 12px 0", color: "#000", fontSize: "20px" }}>Output Cells</h3>
                <div style={{ 
                    backgroundColor: "#fff", 
                    padding: 16, 
                    borderRadius: 4, 
                    border: "1px solid #e9ecef" 
                }}>
                    <div style={{ 
                        display: "grid", 
                        gridTemplateColumns: "1fr 1fr", 
                        gap: 16, 
                        alignItems: "end" 
                    }}>
                        <div>
                            <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Sheet Name</label>
                            <select 
                                value={this.state.outputSheetName || ""} 
                                onChange={(e) => this.setState({ outputSheetName: e.target.value })}
                                style={{ width: "100%", padding: 8, borderRadius: 4, background: "#F0F0F0", border: "none", color:"#666" }}
                            >
                                <option value="">Select sheet name</option>
                                {sheetNames.map(name => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div>
                            <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Output Cells</label>
                            <input 
                                type="text" 
                                placeholder="e.g., F35, F36, F37"
                                value={this.state.outputCells || ""} 
                                onChange={(e) => this.setState({ outputCells: e.target.value })}
                                style={{ width: "100%", padding: 8, border: "1px solid #ddd", borderRadius: 4 }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    buildSaveFileSection = (saveFileSchema) => {
        return (
            <div key="save_file_post_call" style={{ marginBottom: 0 }}>
                <h3 style={{ margin: "0 0 12px 0", color: "#000", fontSize: "20px" }}>Save Option</h3>
                <div style={{ 
                    backgroundColor: "#fff", 
                    padding: 16, 
                    borderRadius: 4, 
                    border: "1px solid #e9ecef" 
                }}>
                    <div style={{ marginBottom: 12 }}>
                        <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>save_file_post_call</label>
                        <input 
                            type="text" 
                            value="FALSE" 
                            disabled={true}
                            style={{ 
                                width: "100%", 
                                padding: 8, 
                                border: "1px solid #ddd", 
                                borderRadius: 4,
                                backgroundColor: "#f5f5f5",
                                color: "#666"
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    };

    getSheetNames = () => {
        if (!this.props.fileInformation) return ["Sheet1"];
        
        const fileKey = Object.keys(this.props.fileInformation)[0];
        if (!fileKey) return ["Sheet1"];
        
        const fileInfo = this.props.fileInformation[fileKey];
        if (!fileInfo || !fileInfo.sheetNames) return ["Sheet1"];
        
        return fileInfo.sheetNames;
    };

    // Transform flattened form data into API-expected format
    transformCalculateBody = function(body) {
        if (!body || (typeof body !== "object")) return body;

        var out = Object.assign({}, body);

        // input_cells: transform from flattened form to API format
        if (body.input_cells && Array.isArray(body.input_cells)) {
            var inMap = {};
            for (var i = 0; i < body.input_cells.length; i++) {
                var item = body.input_cells[i];
                if (!item || typeof item !== "object") continue;
                
                var sheet = item.sheet_name;
                if (!sheet) continue;

                if (!inMap[sheet]) {
                    inMap[sheet] = {};
                }
                
                var cellsArr = item.cells || [];
                for (var j = 0; j < cellsArr.length; j++) {
                    var c = cellsArr[j];
                    if (c == null) continue;
                    var ref = c.cell_name;
                    var val = c.cell_value;
                    if (ref !== undefined && val !== undefined) {
                        inMap[sheet][ref] = val;
                    }
                }
            }
            out.input_cells = inMap;
        }

        // output_cells: transform from flattened form to API format
        if (body.output_cells && Array.isArray(body.output_cells)) {
            var outMap = {};
            for (var k = 0; k < body.output_cells.length; k++) {
                var o = body.output_cells[k];
                if (!o || typeof o !== "object") continue;
                var sheet2 = o.sheet_name;
                if (!sheet2) continue;
                
                if (!outMap[sheet2]) {
                    outMap[sheet2] = [];
                }
                
                var arr = o.cells || [];
                var newCells = arr.map(function(v){ return (typeof v === "string") ? v : String(v); });
                outMap[sheet2] = outMap[sheet2].concat(newCells);
            }
            out.output_cells = outMap;
        }

        return out;
    };

    seedFlat = function(schema) {
      if (!schema) return "";
      var t = schema.type;
      if (t && t.splice && t.length) t = t[0]; // array-of-types guard
      if (schema.enum && schema.enum.length) return schema.enum[0];
      if (schema.default !== undefined) return schema.default;
      if (schema.example !== undefined) return schema.example;

      if (t === "string") return "";
      if (t === "number" || t === "integer") return 0;
      if (t === "boolean") return false;
      if (t === "object") {
        // Special handling for post_formula_actions
        if (schema.properties && schema.properties.post_formula_actions) {
          return {
            post_formula_actions: {
              type: "macro",
              parameters: ""
            },
            macros: "",
            input_cells: {},
            output_cells: {}
          };
        }
        return {};
      }
      if (t === "array") {
        // Special handling for post_formula_actions array
        if (schema.items && schema.items.properties && 
            schema.items.properties.type && schema.items.properties.parameters) {
          return [{
            type: "macro",
            parameters: ""
          }];
        }
        return [];
      }
      return "";
    };

    getAtPath = (obj, pathArr) => {
      var cur = obj;
      for (var i=0;i<pathArr.length;i++){ if (cur==null) return undefined; cur = cur[pathArr[i]]; }
      return cur;
    };

    setAtPath = (obj, pathArr, value) => {
      var cur = obj;
      for (var i = 0; i < pathArr.length - 1; i++) {
        var k = pathArr[i];
        if (cur[k] == null) {
          var nextKey = pathArr[i + 1];
          cur[k] = (typeof nextKey === "number") ? [] : {};
        }
        cur = cur[k];
      }
      cur[pathArr[pathArr.length - 1]] = value;
      return obj;
    };

    onFormValueChange = (pathArr, value) => {
      var next = JSON.parse(JSON.stringify(this.state.formValues || {}));
      this.setAtPath(next, pathArr, value);
      this.setState({ formValues: next });
    };

    seedFromSchemaGeneric = (schema) => {
      if (!schema) return "";
      var t = schema.type; if (t && t.splice && t.length) t = t[0];
      if (schema.enum && schema.enum.length) return schema.enum[0];
      if (schema.default !== undefined) return schema.default;
      if (schema.example !== undefined) return schema.example;
      if (t === "object" || schema.properties) return {};
      if (t === "array" || schema.items) return [];
      if (t === "number" || t === "integer") return 0;
      if (t === "boolean") return false;
      return "";
    };

    renderPrimitive = (label, t, val, onChange, required, placeholder) => {
        var showLabel = (label && String(label).length > 0);
        if (t === "boolean") {
            return (
                <div style={{ marginBottom: 12 }}>
                    {showLabel ? (
                        <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                        {label}
                        </label>
                    ) : null}
                    <input type="checkbox" checked={!!val} onChange={function(e){ onChange(e.target.checked); }} />
                </div>
            );
        }
        var isNum = (t === "number" || t === "integer");
        
        // Add placeholder text for macro-related fields
        var finalPlaceholder = placeholder;
        if (!finalPlaceholder && label && (label.toLowerCase().includes("macro") || label.toLowerCase().includes("parameters"))) {
            finalPlaceholder = "Name of macro";
        }
        
        return (
            <div style={{ marginBottom: 12 }}>
                {showLabel ? (
                <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                    {label}
                </label>
                ) : null}
                <input
                    type={isNum ? "number" : "text"}
                    value={(val !== undefined && val !== null) ? val : (isNum ? 0 : "")}
                    onChange={function(e){ onChange(isNum ? Number(e.target.value) : e.target.value); }}
                    style={{ width: "100%", padding: 8, borderRadius: 4, background: "#F0F0F0", border: "none" }}
                    placeholder={finalPlaceholder || undefined}
                />
            </div>
        );
    };

    renderObjectFields = (schema, pathArr, requiredList) => {
        var props = schema.properties || {}; var self=this;
        return (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
                {Object.keys(props).map(function(k){
                var child = self.resolveRef(self.state.rawSpec, props[k]);
                var t = child && child.type; if (t && t.splice && t.length) t=t[0];
                var val = self.getAtPath(self.state.formValues||{}, pathArr.concat(k));
                var req = (requiredList && requiredList.indexOf(k)!==-1);
    
                // Special case: render sheet_name as a dropdown of sheet names
                if (k === "sheet_name") {
                    var sheets = self.getSheetNames() || [];
                    return (
                    <div key={k} style={{ marginBottom:12 }}>
                        <label style={{ fontWeight:600, display:"block", marginBottom:4 }}>
                            {(child && child.title) ? child.title : self.prettyLabel(k)}
                        </label>
                        <select
                        value={(val!==undefined && val!==null)?val:""}
                        onChange={(e)=>self.onFormValueChange(pathArr.concat(k), e.target.value)}
                        style={{ width:"100%", padding:8, borderRadius:4, background: "#F0F0F0", border: "none", color:"#666" }}
                        >
                        <option value="">Select…</option>
                        {sheets.map(function(name){ return <option key={String(name)} value={name}>{String(name)}</option>; })}
                        </select>
                    </div>
                    );
                }
    
                if (child.enum && child.enum.length){
                    return (
                    <div key={k} style={{ marginBottom:12 }}>
                        <label style={{ fontWeight:600, display:"block", marginBottom:4 }}>
                        {(child && child.title) ? child.title : self.prettyLabel(k)}
                        </label>
                        <select
                        value={(val!==undefined && val!==null)?val:""}
                        onChange={(e)=>self.onFormValueChange(pathArr.concat(k), e.target.value)}
                        style={{ width:"100%", padding:8, borderRadius:4, background: "#F0F0F0", border: "none", color:"#666" }}
                        >
                        <option value="">Select…</option>
                        {child.enum.map(function(opt){ return <option key={String(opt)} value={opt}>{String(opt)}</option>; })}
                        </select>
                    </div>
                    );
                }
                if (t==="object" || child.properties){
                    return (
                    <div key={k} style={{ marginBottom:8 }}>
                        {self.renderObjectFields(child, pathArr.concat(k), child.required)}
                    </div>
                    );
                }
                if (t==="array" || child.items){
                        return self.renderArrayField(child, pathArr.concat(k), (child && child.title) ? child.title : self.prettyLabel(k), req, true);
                    }
                    var ph = null;
                    if (k === "cell_name") ph = "A1 or named_cell";
                    else if (k === "cell_value") ph = "New value for cell";
                    else if (k === "macros") ph = "Name of macro";
                    else if (k === "parameters") ph = "Name of macro";
                    return self.renderPrimitive(
                        (child && child.title) ? child.title : self.prettyLabel(k),
                        t,
                        val,
                        function(v){ self.onFormValueChange(pathArr.concat(k), v); },
                        req,
                        ph
                    );
                })}
            </div>
        );
    };

    renderArrayField = (schema, pathArr, label, required, suppressLabel) => {
        var items = this.resolveRef(this.state.rawSpec, schema.items || {});
        var it = items && items.type; if (it && it.splice && it.length) it = it[0];
        var val = this.getAtPath(this.state.formValues || {}, pathArr);
        val = Array.isArray(val) ? val : [];
        var self = this;
    
        // Special handling for input_cells and output_cells arrays
        if (pathArr.length > 0 && (pathArr[pathArr.length - 1] === "input_cells" || pathArr[pathArr.length - 1] === "output_cells")) {
            const fieldName = pathArr[pathArr.length - 1];
            const isInputCells = fieldName === "input_cells";
            
            // Ensure we have at least one entry
            if (val.length === 0) {
                const seeded = isInputCells 
                    ? { sheet_name: "", cells: [{ cell_name: "", cell_value: "" }] }
                    : { sheet_name: "", cells: [] };
                var next = JSON.parse(JSON.stringify(this.state.formValues || {}));
                this.setAtPath(next, pathArr, [seeded]);
                this.setState({ formValues: next });
                val = [seeded];
            }
    
            const addEntry = () => {
                const seeded = isInputCells 
                    ? { sheet_name: "", cells: [{ cell_name: "", cell_value: "" }] }
                    : { sheet_name: "", cells: [] };
                const next = JSON.parse(JSON.stringify(this.state.formValues || {}));
                const newVal = [...val, seeded];
                this.setAtPath(next, pathArr, newVal);
                this.setState({ formValues: next });
            };
    
            const removeEntry = (index) => {
                const next = JSON.parse(JSON.stringify(this.state.formValues || {}));
                const newVal = [...val];
                newVal.splice(index, 1);
                this.setAtPath(next, pathArr, newVal);
                this.setState({ formValues: next });
            };
    
            const updateEntry = (index, field, value) => {
                const next = JSON.parse(JSON.stringify(this.state.formValues || {}));
                const newVal = [...val];
                newVal[index] = { ...newVal[index], [field]: value };
                this.setAtPath(next, pathArr, newVal);
                this.setState({ formValues: next });
            };
    
            const updateCell = (entryIndex, cellIndex, field, value) => {
                const next = JSON.parse(JSON.stringify(this.state.formValues || {}));
                const newVal = [...val];
                if (!newVal[entryIndex].cells) newVal[entryIndex].cells = [];
                if (!newVal[entryIndex].cells[cellIndex]) newVal[entryIndex].cells[cellIndex] = {};
                newVal[entryIndex].cells[cellIndex] = { ...newVal[entryIndex].cells[cellIndex], [field]: value };
                this.setAtPath(next, pathArr, newVal);
                this.setState({ formValues: next });
            };
    
            return (
                <div>
                    {val.map((entry, entryIndex) => (
                        <div key={entryIndex} style={{ 
                            display: "grid", 
                            gridTemplateColumns: isInputCells ? "1fr 1fr 1fr auto" : "1fr 2fr auto", 
                            gap: 12, 
                            marginBottom: 12,
                            padding: "0",
                            backgroundColor: "transparent",
                            borderRadius: "4px",
                            border: "none"
                        }}>
                            <div>
                                <label style={{ fontWeight: 600, display: "block", marginBottom: 4, fontSize: "12px" }}>Sheet Name</label>
                                <select
                                    value={entry.sheet_name || ""}
                                    onChange={(e) => updateEntry(entryIndex, "sheet_name", e.target.value)}
                                    style={{ width: "100%", padding: 8, borderRadius: 4, background: "#F0F0F0", border: "none", color: "#666" }}
                                >
                                    <option value="">Select…</option>
                                    {(this.getSheetNames() || []).map((name) => <option key={String(name)} value={name}>{String(name)}</option>)}
                                </select>
                            </div>
                            
                            {isInputCells ? (
                                <>
                                    <div>
                                        <label style={{ fontWeight: 600, display: "block", marginBottom: 4, fontSize: "12px" }}>Named Cell or Cell</label>
                                        <input
                                            type="text"
                                            placeholder="A1 or named_cell"
                                            value={(entry.cells && entry.cells[0] && entry.cells[0].cell_name) || ""}
                                            onChange={(e) => updateCell(entryIndex, 0, "cell_name", e.target.value)}
                                            style={{ width: "100%", padding: 8, borderRadius: 4, background: "#F0F0F0", border: "none" }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontWeight: 600, display: "block", marginBottom: 4, fontSize: "12px" }}>Cell Value</label>
                                        <input
                                            type="text"
                                            placeholder="New value for cell"
                                            value={(entry.cells && entry.cells[0] && entry.cells[0].cell_value) || ""}
                                            onChange={(e) => updateCell(entryIndex, 0, "cell_value", e.target.value)}
                                            style={{ width: "100%", padding: 8, borderRadius: 4, background: "#F0F0F0", border: "none" }}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div>
                                    <label style={{ fontWeight: 600, display: "block", marginBottom: 4, fontSize: "12px" }}>Cells</label>
                                    <input
                                        type="text"
                                        placeholder="Ex. A1, A2, C5..."
                                        value={typeof entry.cells === 'string' ? entry.cells : (Array.isArray(entry.cells) ? entry.cells.join(", ") : "")}
                                        onChange={(e) => {
                                            // Store the raw string value without processing
                                            updateEntry(entryIndex, "cells", e.target.value);
                                        }}
                                        style={{ width: "100%", padding: 8, borderRadius: 4, background: "#F0F0F0", border: "none" }}
                                    />
                                </div>
                            )}
                            
                            <div style={{ display: "flex", alignItems: "end" }}>
                                <button
                                    onClick={() => removeEntry(entryIndex)}
                                    disabled={val.length <= 1}
                                    style={{
                                        background: "#9696A0",
                                        color: "white",
                                        border: "none",
                                        padding: "8px 12px",
                                        borderRadius: "4px",
                                        cursor: val.length <= 1 ? "not-allowed" : "pointer",
                                        opacity: val.length <= 1 ? 0.5 : 1,
                                        fontSize: "12px"
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    ))}
                    
                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "12px" }}>
                        <button
                            onClick={addEntry}
                            style={{
                                background: "#6FBB98",
                                color: "white",
                                border: "none",
                                padding: "8px 12px",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px",
                                marginTop: "12px"
                            }}
                        >
                            + Add Sheet
                        </button>
                    </div>
                </div>
            );
        }
    
        // Ensure a single item exists for demo (existing logic for other array types)
        if (val.length === 0) {
            var seeded = this.seedFromSchemaGeneric(items);
            if (seeded === undefined || seeded === null) {
                if (it === "object") seeded = {};
                else if (it === "string") seeded = "";
                else if (it === "number" || it === "integer") seeded = 0;
                else if (it === "boolean") seeded = false;
                else seeded = "";
            }
            var next = JSON.parse(JSON.stringify(this.state.formValues || {}));
            this.setAtPath(next, pathArr, [seeded]);
            this.setState({ formValues: next });
            val = [seeded];
        }
    
        // ... rest of existing renderArrayField method for other array types ...
        if (it === "string" || it === "number" || it === "integer" || it === "boolean") {
            var keyStr = pathArr.join(".");
            var display = (this.state.textInputs && this.state.textInputs[keyStr] !== undefined)
              ? this.state.textInputs[keyStr]
              : val.join(", ");
            return (
                <div style={{ marginBottom: 12 }}>
                    {(!suppressLabel && label) ? (
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>
                        {label}
                    </div>
                    ) : null}
                    <input
                        type="text"
                        placeholder={(pathArr && pathArr.length && pathArr[pathArr.length-1] === "cells")
                        ? "Ex. A1, A2, C5..."
                        : "Comma separated"}
                        value={display}
                        onChange={(e) => {
                        const s = e.target.value;
                        const nextText = Object.assign({}, this.state.textInputs || {});
                        nextText[keyStr] = s;
                        const list = s.split(",").map(x => x.trim()).filter(x => x.length);
                        this.onFormValueChange(pathArr, list);
                        this.setState({ textInputs: nextText });
                        }}
                        style={{ width: "100%", padding: 8, borderRadius: 4, background: "#F0F0F0", border: "none" }}
                    />
                </div>
            );
        }
    
        // array of objects → render first item only, flattened horizontally
        var props = items.properties || {};
        
        if (pathArr.length > 0 && pathArr[pathArr.length - 1] === "input_cells") {
            return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
                    {/* Sheet Name */}
                    <div>
                        <label style={{ fontWeight:600, display:"block", marginBottom:4 }}>
                            Sheet Name
                        </label>
                        <select
                            value={this.getAtPath(this.state.formValues || {}, pathArr.concat(0, "sheet_name")) || ""}
                            onChange={(e)=>this.onFormValueChange(pathArr.concat(0, "sheet_name"), e.target.value)}
                            style={{ width:"100%", padding:8, borderRadius:4, background: "#F0F0F0", border: "none", color:"#666" }}
                        >
                            <option value="">Select…</option>
                            {(this.getSheetNames() || []).map(function(name){ return <option key={String(name)} value={name}>{String(name)}</option>; })}
                        </select>
                    </div>
                    
                    {/* Cell Name */}
                    <div>
                        <label style={{ fontWeight:600, display:"block", marginBottom:4 }}>
                            Named Cell or Cell
                        </label>
                        <input
                            type="text"
                            placeholder="A1 or named_cell"
                            value={this.getAtPath(this.state.formValues || {}, pathArr.concat(0, "cells", 0, "cell_name")) || ""}
                            onChange={(e)=>this.onFormValueChange(pathArr.concat(0, "cells", 0, "cell_name"), e.target.value)}
                            style={{ width:"100%", padding:8, borderRadius:4, background: "#F0F0F0", border: "none" }}
                        />
                    </div>
                    
                    {/* Cell Value */}
                    <div>
                        <label style={{ fontWeight:600, display:"block", marginBottom:4 }}>
                            Cell Value
                        </label>
                        <input
                            type="text"
                            placeholder="New value for cell"
                            value={this.getAtPath(this.state.formValues || {}, pathArr.concat(0, "cells", 0, "cell_value")) || ""}
                            onChange={(e)=>this.onFormValueChange(pathArr.concat(0, "cells", 0, "cell_value"), e.target.value)}
                            style={{ width:"100%", padding:8, borderRadius:4, background: "#F0F0F0", border: "none" }}
                        />
                    </div>
                </div>
            );
        }
        
        // Regular array rendering for other cases
        return (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
                {Object.keys(props).map(function(k){
                    var child = self.resolveRef(self.state.rawSpec, props[k]);
                    var t = child && child.type; if (t && t.splice && t.length) t=t[0];
                    var subPath = pathArr.concat(0, k);
                    var val2 = self.getAtPath(self.state.formValues || {}, subPath);
    
                    // sheet_name dropdown
                    if (k === "sheet_name") {
                        var sheets = self.getSheetNames() || [];
                        return (
                            <div key={k}>
                                <label style={{ fontWeight:600, display:"block", marginBottom:4 }}>
                                    {(child && child.title) ? child.title : self.prettyLabel(k)}
                                </label>
                                <select
                                    value={(val2!==undefined && val2!==null)?val2:""}
                                    onChange={(e)=>self.onFormValueChange(subPath, e.target.value)}
                                    style={{ width:"100%", padding:8, borderRadius:4, background: "#F0F0F0", border: "none", color:"#666" }}
                                >
                                    <option value="">Select…</option>
                                    {sheets.map(function(name){ return <option key={String(name)} value={name}>{String(name)}</option>; })}
                                </select>
                            </div>
                        );
                    }
    
                    // nested arrays inside item (e.g., cells)
                    if (t === "array" || (child && child.items)) {
                        var itemType = child.items && child.items.type;
                        // primitive list (e.g., output_cells.cells)
                        if (itemType === "string" || itemType === "number" || itemType === "integer" || itemType === "boolean") {
                            var keyStr2 = subPath.join(".");
                            var display2 = (self.state.textInputs && self.state.textInputs[keyStr2] !== undefined)
                                ? self.state.textInputs[keyStr2]
                                : (Array.isArray(val2) ? val2.join(", ") : "");
                            return (
                                <div key={k}>
                                    <label style={{ fontWeight:600, display:"block", marginBottom:4 }}>
                                        {(child && child.title) ? child.title : self.prettyLabel(k)}
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Ex. A1, A2, C5..."
                                        value={display2}
                                        onChange={function(e){
                                            var s = e.target.value;
                                            var nextText = Object.assign({}, self.state.textInputs || {});
                                            nextText[keyStr2] = s;
                                            var list = s.split(",").map(function(x){ return x.trim(); }).filter(function(x){ return x.length; });
                                            self.onFormValueChange(subPath, list);
                                            self.setState({ textInputs: nextText });
                                        }}
                                        style={{ width:"100%", padding:8, borderRadius:4, background: "#F0F0F0", border: "none" }}
                                    />
                                </div>
                            );
                        }
                        var subProps = (child.items && child.items.properties) || {};
                        return (
                            <div key={k} style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))", gap:12 }}>
                                {Object.keys(subProps).map(function(sk){
                                    var subChild = self.resolveRef(self.state.rawSpec, subProps[sk]);
                                    var st = subChild && subChild.type; if (st && st.splice && st.length) st = st[0];
                                    var leafPath = subPath.concat(0, sk);
                                    var leafVal = self.getAtPath(self.state.formValues || {}, leafPath);
                                    var ph = null;
                                    if (sk === "cell_name") ph = "A1 or named_cell";
                                    else if (sk === "cell_value") ph = "New value for cell";
                                    else if (sk === "macros") ph = "Name of macro";
                                    else if (sk === "parameters") ph = "Name of macro";
                                    return (
                                        <div key={sk}>
                                            {self.renderPrimitive((subChild && subChild.title) ? subChild.title : self.prettyLabel(sk),
                                                st,
                                                leafVal,
                                                function(v){ self.onFormValueChange(leafPath, v); },
                                                false,
                                                ph)}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    }
    
                    // primitive/enum inside item
                    if (child && child.enum && child.enum.length) {
                        return (
                            <div key={k}>
                                <label style={{ fontWeight:600, display:"block", marginBottom:4 }}>
                                    {(child && child.title) ? child.title : self.prettyLabel(k)}
                                </label>
                                <select
                                    value={(val2!==undefined && val2!==null)?val2:""}
                                    onChange={(e)=>self.onFormValueChange(subPath, e.target.value)}
                                    style={{ width:"100%", padding:8, borderRadius:4, background: "#F0F0F0", border: "none", color:"#666" }}
                                >
                                    <option value="">Select…</option>
                                    {child.enum.map(function(opt){ return <option key={String(opt)} value={opt}>{String(opt)}</option>; })}
                                </select>
                            </div>
                        );
                    }
                    var ph2 = null;
                    if (k === "cell_name") ph2 = "A1 or named_cell";
                    else if (k === "cell_value") ph2 = "New value for cell";
                    else if (k === "macros") ph2 = "Name of macro";
                    else if (k === "parameters") ph2 = "Name of macro";
                    return (
                        <div key={k}>
                            {self.renderPrimitive((child && child.title) ? child.title : self.prettyLabel(k),
                                t,
                                val2,
                                function(v){ self.onFormValueChange(subPath, v); },
                                false,
                                ph2)}
                        </div>
                    );
                })}
            </div>
        );
    };

    // ----- Macro.yaml helpers (map-style schemas) -----
    isMacroStyleRequest = () => {
        const { rawSpec, selectedPath, selectedMethod } = this.state;
        if (!rawSpec || !selectedPath || !selectedMethod) return false;
        const schema = this.getRequestBodySchema(rawSpec, selectedPath, selectedMethod);
        if (!schema || !schema.properties) return false;
        const inS = this.resolveRef(rawSpec, schema.properties.input_cells);
        const outS = this.resolveRef(rawSpec, schema.properties.output_cells);
        const isAdditionalMap = (s) => s && s.type === "object" && s.additionalProperties && !s.properties;
        return isAdditionalMap(inS) || isAdditionalMap(outS);
    };

    renderMacroInputCells = (fieldName) => {
        const v = (this.state.formValues && this.state.formValues[fieldName]) || {};
        const sheets = this.getSheetNames() || [];
        
        // Ensure we have at least one input cell entry
        if (!v.entries || !Array.isArray(v.entries) || v.entries.length === 0) {
            const next = Object.assign({}, this.state.formValues || {});
            next[fieldName] = { entries: [{ sheet_name: "", cell_name: "", cell_value: "" }] };
            this.setState({ formValues: next });
            return <div>Loading...</div>;
        }
    
        const addInputEntry = () => {
            const next = Object.assign({}, this.state.formValues || {});
            next[fieldName] = Object.assign({}, v, {
                entries: [...(v.entries || []), { sheet_name: "", cell_name: "", cell_value: "" }]
            });
            this.setState({ formValues: next });
        };
    
        const removeInputEntry = (index) => {
            const next = Object.assign({}, this.state.formValues || {});
            const newEntries = [...(v.entries || [])];
            newEntries.splice(index, 1);
            next[fieldName] = Object.assign({}, v, { entries: newEntries });
            this.setState({ formValues: next });
        };
    
        const updateInputEntry = (index, field, value) => {
            const next = Object.assign({}, this.state.formValues || {});
            const newEntries = [...(v.entries || [])];
            newEntries[index] = Object.assign({}, newEntries[index], { [field]: value });
            next[fieldName] = Object.assign({}, v, { entries: newEntries });
            this.setState({ formValues: next });
        };
    
        return (
            <div>
                {(v.entries || []).map((entry, index) => (
                    <div key={index} style={{ 
                        display: "grid", 
                        gridTemplateColumns: "1fr 1fr 1fr auto", 
                        gap: 12, 
                        marginBottom: 12,
                        padding: "0",
                        backgroundColor: "transparent",
                        borderRadius: "4px",
                        border: "none"
                    }}>
                        <div>
                            <label style={{ fontWeight: 600, display: "block", marginBottom: 4, fontSize: "12px" }}>Sheet Name</label>
                            <select
                                value={entry.sheet_name || ""}
                                onChange={(e) => updateInputEntry(index, "sheet_name", e.target.value)}
                                style={{ width: "100%", padding: 8, borderRadius: 4, background: "#F0F0F0", border: "none", color: "#666" }}
                            >
                                <option value="">Select…</option>
                                {sheets.map((name) => <option key={String(name)} value={name}>{String(name)}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontWeight: 600, display: "block", marginBottom: 4, fontSize: "12px" }}>Named Cell or Cell</label>
                            <input
                                type="text"
                                placeholder="A1 or named_cell"
                                value={entry.cell_name || ""}
                                onChange={(e) => updateInputEntry(index, "cell_name", e.target.value)}
                                style={{ width: "100%", padding: 8, borderRadius: 4, background: "#F0F0F0", border: "none" }}
                            />
                        </div>
                        <div>
                            <label style={{ fontWeight: 600, display: "block", marginBottom: 4, fontSize: "12px" }}>Cell Value</label>
                            <input
                                type="text"
                                placeholder="New value for cell"
                                value={entry.cell_value || ""}
                                onChange={(e) => updateInputEntry(index, "cell_value", e.target.value)}
                                style={{ width: "100%", padding: 8, borderRadius: 4, background: "#F0F0F0", border: "none" }}
                            />
                        </div>
                        <div style={{ display: "flex", alignItems: "end" }}>
                            <button
                                onClick={() => removeInputEntry(index)}
                                disabled={(v.entries || []).length <= 1}
                                style={{
                                    background: "#9696A0",
                                    color: "white",
                                    border: "none",
                                    padding: "8px 12px",
                                    borderRadius: "4px",
                                    cursor: (v.entries || []).length <= 1 ? "not-allowed" : "pointer",
                                    opacity: (v.entries || []).length <= 1 ? 0.5 : 1,
                                    fontSize: "12px"
                                }}
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                ))}
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: "12px" }}>
                    <button
                        onClick={addInputEntry}
                        style={{
                            background: "#6FBB98",
                            color: "white",
                            border: "none",
                            padding: "8px 12px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            marginTop: "12px"
                        }}
                    >
                        + Add Sheet
                    </button>
                </div>
            </div>
        );
    };

    renderMacroOutputCells = (fieldName) => {
        const v = (this.state.formValues && this.state.formValues[fieldName]) || {};
        const sheets = this.getSheetNames() || [];
        
        if (!v.entries || !Array.isArray(v.entries) || v.entries.length === 0) {
            const next = Object.assign({}, this.state.formValues || {});
            next[fieldName] = { entries: [{ sheet_name: "", cells: "" }] };
            this.setState({ formValues: next });
            return <div>Loading...</div>;
        }
    
        const addOutputEntry = () => {
            const next = Object.assign({}, this.state.formValues || {});
            next[fieldName] = Object.assign({}, v, {
                entries: [...(v.entries || []), { sheet_name: "", cells: "" }]
            });
            this.setState({ formValues: next });
        };
    
        const removeOutputEntry = (index) => {
            const next = Object.assign({}, this.state.formValues || {});
            const newEntries = [...(v.entries || [])];
            newEntries.splice(index, 1);
            next[fieldName] = Object.assign({}, v, { entries: newEntries });
            this.setState({ formValues: next });
        };
    
        const updateOutputEntry = (index, field, value) => {
            const next = Object.assign({}, this.state.formValues || {});
            const newEntries = [...(v.entries || [])];
            newEntries[index] = Object.assign({}, newEntries[index], { [field]: value });
            next[fieldName] = Object.assign({}, v, { entries: newEntries });
            this.setState({ formValues: next });
        };
    
        return (
            <div>
                {(v.entries || []).map((entry, index) => (
                    <div key={index} style={{ 
                        display: "grid", 
                        gridTemplateColumns: "1fr 2fr auto", 
                        gap: 12, 
                        marginBottom: 12,
                        padding: "0",
                        backgroundColor: "transparent",
                        borderRadius: "4px",
                        border: "none"
                    }}>
                        <div>
                            <label style={{ fontWeight: 600, display: "block", marginBottom: 4, fontSize: "12px" }}>Sheet Name</label>
                            <select
                                value={entry.sheet_name || ""}
                                onChange={(e) => updateOutputEntry(index, "sheet_name", e.target.value)}
                                style={{ width: "100%", padding: 8, borderRadius: 4, backgroundColor: "#F0F0F0", color: "#666", border: "none" }}
                            >
                                <option value="">Select…</option>
                                {sheets.map((name) => <option key={String(name)} value={name}>{String(name)}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontWeight: 600, display: "block", marginBottom: 4, fontSize: "12px" }}>Cells</label>
                            <input
                                type="text"
                                placeholder="Ex. A1, A2, C5..."
                                value={entry.cells || ""}
                                onChange={(e) => updateOutputEntry(index, "cells", e.target.value)}
                                style={{ width: "100%", padding: 8, borderRadius: 4, backgroundColor: "#F0F0F0", color: "#000", border: "none" }}
                            />
                        </div>
                        <div style={{ display: "flex", alignItems: "end" }}>
                            <button
                                onClick={() => removeOutputEntry(index)}
                                disabled={(v.entries || []).length <= 1}
                                style={{
                                    background: "#9696A0",
                                    color: "white",
                                    border: "none",
                                    padding: "8px 12px",
                                    borderRadius: "4px",
                                    cursor: (v.entries || []).length <= 1 ? "not-allowed" : "pointer",
                                    opacity: (v.entries || []).length <= 1 ? 0.5 : 1,
                                    fontSize: "12px"
                                }}
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                ))}
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: "12px" }}>
                    <button
                        onClick={addOutputEntry}
                        style={{
                            background: "#6FBB98",
                            color: "white",
                            border: "none",
                            padding: "8px 12px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            marginTop: "12px"
                        }}
                    >
                        + Add Sheet
                    </button>
                </div>
            </div>
        );
    };

    renderParamCardsWithInputs = (spec, path, method) => {
        var schema = this.getRequestBodySchema(spec, path, method);
        if (!schema) return null;
        var params = this.getRequestBodyParams(spec, path, method);
        var self=this;
      
        if (!params.length){
          var t = schema.type; if (t && t.splice && t.length) t=t[0];
          if (t==="object" || schema.properties) {
            return (
              <div style={{ marginBottom:20 }}>
                <h3 style={{ margin:"0 0 12px 0", color:"#000", fontSize:"20px" }}>Body</h3>
                <div style={{ background:"#fff", padding:16, borderRadius:4, border:"1px solid #e9ecef" }}>
                  {self.renderObjectFields(schema, [], schema.required)}
                </div>
              </div>
            );
          }
          return (
            <div style={{ marginBottom:20 }}>
              <h3 style={{ margin:"0 0 12px 0", color:"#000", fontSize:"20px" }}>Body</h3>
              <div style={{ background:"#fff", padding:16, borderRadius:4, border:"1px solid #e9ecef" }}>
                { (t==="array"||schema.items)
                  ? self.renderArrayField(schema, ["(root)"], "", true, true)  // no inner title
                  : self.renderPrimitive("", t, self.state.formValues["(root)"], function(v){
                      var n={}; n["(root)"]=v; self.setState({ formValues:n });
                    }, true, null)
                }
              </div>
            </div>
          );
        }
      
        // Define the desired order for macro style requests
        const getParamOrder = (paramName) => {
          const order = {
            'input_cells': 1,
            'output_cells': 2,
            'post_formula_actions': 3,
            'macros': 4,
            'save_file_post_call': 5
          };
          return order[paramName] || 999; // Default to end for unknown params
        };
      
        // Sort params by the desired order
        const sortedParams = params.sort((a, b) => {
          return getParamOrder(a.name) - getParamOrder(b.name);
        });
      
        return (
          <div>
              {sortedParams.map(function(p){
                  var ps = self.resolveRef(spec, p.schema);
                  var t = ps && ps.type; if (t && t.splice && t.length) t=t[0];
      
                  var isAdditionalMap = (t === "object") && !!(ps && ps.additionalProperties) && !ps.properties;
      
                  return (
                  <div key={p.name} style={{ marginBottom: 20 }}>
                      <div style={{ background:"#fff", padding:16, borderRadius:4, border:"1px solid #e9ecef" }}>
                      <div style={{ fontWeight: 600, fontSize: "16px", margin: "0 0 12px 0", color: "#161e16" }}>
                          {p.name}
                      </div>
                          { ps && ps.description ? (
                              <div style={{ color: "#666", fontSize: "13px", margin: "0 0 12px 0" }}>
                                  {ps.description}
                              </div>
                          ) : null }
                          { isAdditionalMap ? (
                              p.name === "input_cells"
                              ? self.renderMacroInputCells("input_cells")
                              : self.renderMacroOutputCells("output_cells")
                          ) : (
                              p.name === "save_file_post_call" ? (
                              <input
                                  type="text"
                                  value="FALSE"
                                  disabled
                                  style={{ width:"100%", padding:8, borderRadius:4, backgroundColor:"#F0F0F0", color:"#666", border: "none" }}
                              />
                              ) : (
                              (t==="object"||ps.properties) ? self.renderObjectFields(ps, [p.name], ps.required)
                              : (t === "array" || ps.items)
                                  ? self.renderArrayField(ps, [p.name], "", p.required, true)
                                  : self.renderPrimitive("", t, self.state.formValues[p.name], function(v){
                                      var next = Object.assign({}, self.state.formValues); next[p.name] = v; self.setState({ formValues: next });
                                  }, p.required)
                              )
                          )}
                      </div>
                  </div>
                  );
              })}
          </div>
        );
    };

    // Build JSON request body from form data
    buildRequestBody = function() {
        var fv = this.state.formValues || {};
        var body = (fv["(root)"] !== undefined) ? fv["(root)"] : fv;
    
        // Debug
        console.log("Current formValues:", this.state.formValues);
        console.log("Current body:", body);
    
        var isMacroStyle = this.isMacroStyleRequest();
    
        if (isMacroStyle) {
            var out = Object.assign({}, body);
    
            // Fix post_formula_actions structure - handle the array case properly
            if (out.post_formula_actions && Array.isArray(out.post_formula_actions)) {
                out.post_formula_actions = out.post_formula_actions.map(action => {
                    if (action && typeof action === 'object') {
                        // Ensure parameters is an object with name property
                        if (action.parameters && typeof action.parameters === 'string') {
                            return {
                                type: action.type || 'macro',
                                parameters: { name: action.parameters }
                            };
                        }
                        // If parameters is already an object but missing name, add it
                        if (action.parameters && typeof action.parameters === 'object' && !action.parameters.name) {
                            return {
                                type: action.type || 'macro',
                                parameters: { name: action.parameters.value || action.parameters.param || 'macro_name' }
                            };
                        }
                    }
                    return action;
                });
            } else if (out.post_formula_actions && out.post_formula_actions.type && out.post_formula_actions.parameters) {
                // Handle flat structure case
                out.post_formula_actions = [{
                    type: out.post_formula_actions.type,
                    parameters: {
                        name: out.post_formula_actions.parameters
                    }
                }];
            }
    
            if (out.input_cells && out.input_cells.entries && Array.isArray(out.input_cells.entries)) {
                const inputCellsObj = {};
                out.input_cells.entries.forEach(entry => {
                    if (entry.sheet_name && entry.cell_name && entry.cell_value !== undefined) {
                        if (!inputCellsObj[entry.sheet_name]) {
                            inputCellsObj[entry.sheet_name] = {};
                        }
                        inputCellsObj[entry.sheet_name][entry.cell_name] = entry.cell_value;
                    }
                });
                out.input_cells = inputCellsObj;
            }
    
            // Handle new multiple sheet structure for output_cells
            if (out.output_cells && out.output_cells.entries && Array.isArray(out.output_cells.entries)) {
                const outputCellsObj = {};
                out.output_cells.entries.forEach(entry => {
                    if (entry.sheet_name && entry.cells) {
                        const cellsArr = String(entry.cells || "").split(",").map(x => x.trim()).filter(Boolean);
                        if (cellsArr.length > 0) {
                            outputCellsObj[entry.sheet_name] = cellsArr;
                        }
                    }
                });
                out.output_cells = outputCellsObj;
            }
    
            if (out.input_cells && out.input_cells.sheet_name && !out.input_cells.entries) {
                var s1 = out.input_cells.sheet_name;
                var cn = out.input_cells.cell_name;
                var cv = out.input_cells.cell_value;
                if (s1 && cn !== undefined && cn !== "") {
                    out.input_cells = {};
                    out.input_cells[s1] = {};
                    out.input_cells[s1][cn] = cv;
                }
            }
    
            if (out.output_cells && out.output_cells.sheet_name && !out.output_cells.entries) {
                var s2 = out.output_cells.sheet_name;
                var cellsArr = Array.isArray(out.output_cells.cells)
                    ? out.output_cells.cells
                    : String(out.output_cells.cells || "").split(",").map(function(x){ return x.trim(); }).filter(Boolean);
                if (s2 && cellsArr.length) {
                    out.output_cells = {};
                    out.output_cells[s2] = cellsArr;
                }
            }
    
            return out;
        }
    
        if (body && (Array.isArray(body.input_cells) || Array.isArray(body.output_cells))) {
            var reconstructed = {
                input_cells: [],
                output_cells: [],
                save_file_post_call: false
            };
    
            // Fix post_formula_actions structure for non-macro style
            if (body.post_formula_actions) {
                if (Array.isArray(body.post_formula_actions)) {
                    reconstructed.post_formula_actions = body.post_formula_actions.map(action => {
                        if (action && typeof action === 'object') {
                            if (action.parameters && typeof action.parameters === 'string') {
                                return {
                                    type: action.type || 'macro',
                                    parameters: { name: action.parameters }
                                };
                            }
                            if (action.parameters && typeof action.parameters === 'object' && !action.parameters.name) {
                                return {
                                    type: action.type || 'macro',
                                    parameters: { name: action.parameters.value || action.parameters.param || 'macro_name' }
                                };
                            }
                        }
                        return action;
                    });
                } else if (body.post_formula_actions.type && body.post_formula_actions.parameters) {
                    reconstructed.post_formula_actions = [{
                        type: body.post_formula_actions.type,
                        parameters: {
                            name: body.post_formula_actions.parameters
                        }
                    }];
                }
            }
    
            if (Array.isArray(body.input_cells)) {
                for (var i = 0; i < body.input_cells.length; i++) {
                    var item = body.input_cells[i];
                    if (item && item.sheet_name) {
                        var inputItem = { sheet_name: item.sheet_name, cells: [] };
                        if (Array.isArray(item.cells)) {
                            for (var j = 0; j < item.cells.length; j++) {
                                var cell = item.cells[j];
                                if (cell && cell.cell_name && cell.cell_value !== undefined) {
                                    inputItem.cells.push({ cell_name: cell.cell_name, cell_value: cell.cell_value });
                                }
                            }
                        }
                        if (inputItem.cells.length > 0) reconstructed.input_cells.push(inputItem);
                    }
                }
            }
    
            if (Array.isArray(body.output_cells)) {
                for (var k = 0; k < body.output_cells.length; k++) {
                    var outItem = body.output_cells[k];
                    if (outItem && outItem.sheet_name) {
                        var outputItem = { 
                            sheet_name: outItem.sheet_name, 
                            cells: [] 
                        };
                        
                        // Handle both string and array formats for cells
                        if (typeof outItem.cells === 'string') {
                            // Convert comma-separated string to array
                            outputItem.cells = outItem.cells.split(',').map(x => x.trim()).filter(Boolean);
                        } else if (Array.isArray(outItem.cells)) {
                            // Already an array, use as-is
                            outputItem.cells = outItem.cells.filter(function(c){ return c && c.length > 0; });
                        }
                        
                        if (outputItem.cells.length > 0) reconstructed.output_cells.push(outputItem);
                    }
                }
            }
    
            console.log("Reconstructed body:", reconstructed);
            return this.transformCalculateBody(reconstructed);
        }
    
        return body;
    };

    // Replace the single toggle method with individual ones
    toggleAccessKeyVisibility = () => {
        this.setState(prevState => ({
            showAccessKey: !prevState.showAccessKey
        }));
    };

    toggleSecretKeyVisibility = () => {
        this.setState(prevState => ({
            showSecretKey: !prevState.showSecretKey
        }));
    };

    buildSpecUrl = () => {
        const { server, selectedPath, pathParams } = this.state;
        const filledPath = this.fillPath(selectedPath, pathParams);
        const baseUrl = `${server}${filledPath}`;
        
        let specUrl = baseUrl.replace(/\/calculate\//, '/spec/').replace(/\/ai\//, '/spec/');
        
        if (!specUrl.includes('/spec/')) {
            const pkMatch = selectedPath.match(/\{pk\}/);
            if (pkMatch && this.props.fileHash) {
                specUrl = specUrl.replace(/\{pk\}/, this.props.fileHash);
                specUrl = specUrl.replace(/\/[^\/]+\/[^\/]+\/$/, `/spec/${this.props.fileHash}/`);
            }
        }
        
        return specUrl;
    };

    getAuthHeaders = () => {
        const { headerParams } = this.state;
        const headers = { "Content-Type": "application/json" };
        
        if (headerParams.AccessKey || headerParams.accessKey) {
            headers["accessKey"] = headerParams.AccessKey || headerParams.accessKey;
        }
        if (headerParams.SecretKey || headerParams.secretKey) {
            headers["secretKey"] = headerParams.SecretKey || headerParams.secretKey;
        }
        
        return headers;
    };

    handleDownloadSpec = async () => {
        try {
            this.setState({ downloading: true });
            const specUrl = this.buildSpecUrl();
            const headers = this.getAuthHeaders();

            console.log("Downloading spec from:", specUrl);
            console.log("Using headers:", headers);

            const res = await fetch(specUrl, {
                method: "POST",
                headers,
                body: JSON.stringify({})
            });
            
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const specJson = await res.json();
            const yamlStr = yaml.dump(specJson, { noRefs: true, lineWidth: -1 });

            const blob = new Blob([yamlStr], { type: "application/x-yaml" });
            const href = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = href;
            a.download = "openapi-spec.yaml";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(href);

            console.log("OpenAPI spec downloaded successfully");
        } catch (e) {
            console.error(`Failed to download spec: ${e.message}`);
        } finally {
            this.setState({ downloading: false });
        }
    };
}

export default ApiPlaygroundClassic;
